/**
 * Item Sources Store
 *
 * Loads and caches the workspace's configurable idea submission sources.
 * Used by SubmitIdea, ItemSources settings page, and the embed token UI.
 */

import { create } from 'zustand';
import {
  listItemSources,
  setItemSourceActive,
  createItemSource,
  updateItemSourceLabel,
  deleteItemSource,
  updateItemSourceOrder,
  type WorkspaceItemSource,
} from '../../lib/itemSourcesService';

interface ItemSourcesState {
  sources: WorkspaceItemSource[];
  loading: boolean;
  error: string | null;
  loadedWorkspaceId: string | null;

  fetchSources: (workspaceId: string) => Promise<void>;
  getActiveSources: () => WorkspaceItemSource[];
  toggleActive: (id: string, isActive: boolean) => Promise<void>;
  addSource: (workspaceId: string, label: string) => Promise<void>;
  renameSource: (id: string, label: string) => Promise<void>;
  removeSource: (id: string) => Promise<void>;
  reorder: (workspaceId: string, orderedIds: string[]) => Promise<void>;
}

export const useItemSourcesStore = create<ItemSourcesState>((set, get) => ({
  sources: [],
  loading: false,
  error: null,
  loadedWorkspaceId: null,

  fetchSources: async (workspaceId) => {
    if (get().loadedWorkspaceId === workspaceId && get().sources.length > 0) return;
    set({ loading: true, error: null });
    const { data, error } = await listItemSources(workspaceId);
    set({ sources: data, loading: false, error, loadedWorkspaceId: workspaceId });
  },

  getActiveSources: () => get().sources.filter((s) => s.isActive),

  toggleActive: async (id, isActive) => {
    set((state) => ({
      sources: state.sources.map((s) => (s.id === id ? { ...s, isActive } : s)),
    }));
    const { error } = await setItemSourceActive(id, isActive);
    if (error) {
      // Revert on failure
      set((state) => ({
        sources: state.sources.map((s) => (s.id === id ? { ...s, isActive: !isActive } : s)),
        error,
      }));
    }
  },

  addSource: async (workspaceId, label) => {
    const value = label
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    const maxOrder = Math.max(0, ...get().sources.map((s) => s.sortOrder));
    const { data, error } = await createItemSource(workspaceId, value, label.trim(), maxOrder + 1);
    if (error) {
      set({ error });
      return;
    }
    if (data) {
      set((state) => ({ sources: [...state.sources, data] }));
    }
  },

  renameSource: async (id, label) => {
    set((state) => ({
      sources: state.sources.map((s) => (s.id === id ? { ...s, label } : s)),
    }));
    const { error } = await updateItemSourceLabel(id, label);
    if (error) set({ error });
  },

  removeSource: async (id) => {
    const prev = get().sources;
    set((state) => ({ sources: state.sources.filter((s) => s.id !== id) }));
    const { error } = await deleteItemSource(id);
    if (error) {
      set({ sources: prev, error });
    }
  },

  reorder: async (workspaceId, orderedIds) => {
    set((state) => {
      const byId = new Map(state.sources.map((s) => [s.id, s]));
      const reordered = orderedIds
        .map((id, i) => {
          const s = byId.get(id);
          return s ? { ...s, sortOrder: i } : null;
        })
        .filter(Boolean) as WorkspaceItemSource[];
      const rest = state.sources.filter((s) => !orderedIds.includes(s.id));
      return { sources: [...reordered, ...rest] };
    });
    await Promise.all(
      orderedIds.map((id, i) => updateItemSourceOrder(id, i))
    );
    void workspaceId;
  },
}));
